import { differenceInDays, differenceInMonths } from "date-fns";
import { parse } from "date-fns";

const createDateParser =
  (formatString: string) => (dateString: string) =>
    parse(dateString, formatString, new Date());

// Define types for better type safety
type TrendPeriod =
  | "trends-yearly"
  | "trends-quarterly"
  | "trends-monthly"
  | "trends-weekly"
  | "trends-daily"
  | "trends-hourly"
  | "none";

interface ChildWidget {
  url: string;
  [key: string]: any; // Allow other properties
}

interface ChildWidgetWithTrendPeriod extends ChildWidget {
  trendPeriod: TrendPeriod;
}

const trendPeriodKeys: TrendPeriod[] = [
  "trends-yearly",
  "trends-quarterly",
  "trends-monthly",
  "trends-weekly",
  "trends-daily",
  "trends-hourly",
];

const getTrendPeriod = (key: string): TrendPeriod => {
  return trendPeriodKeys.find((d) => key.includes(d)) ?? "none";
};

const sectionTabTitles: Record<TrendPeriod, string> = {
  "trends-yearly": "Yearly",
  "trends-quarterly": "Quarterly",
  "trends-monthly": "Monthly",
  "trends-weekly": "Weekly",
  "trends-daily": "Daily",
  "trends-hourly": "Hourly",
  none: " ",
};

const getSectionTabTitles = (key: string): string => {
  const trendPeriod = getTrendPeriod(key);
  return sectionTabTitles[trendPeriod];
};

const urlSortOrder: Record<TrendPeriod, number> = {
  "trends-yearly": 1,
  "trends-quarterly": 2,
  "trends-monthly": 3,
  "trends-weekly": 4,
  "trends-daily": 5,
  "trends-hourly": 6,
  none: 0,
};

const filterWidgetUrlsBySelectedTimePeriod = (
  selectedTimeRange: [string, string],
  childWidgets: ChildWidget[]
): ChildWidgetWithTrendPeriod[] => {
  const parseDate = createDateParser("yyyy-MM-dd");
  const startTime = parseDate(selectedTimeRange[0]);
  const endTime = parseDate(selectedTimeRange[1]);
  const dayDiff = differenceInDays(endTime, startTime);
  const monthDiff = differenceInMonths(endTime, startTime);

  const childWidgetsWTrendPeriod: ChildWidgetWithTrendPeriod[] =
    childWidgets.map((d) => ({
      ...d,
      trendPeriod: getTrendPeriod(d.url),
    }));

  childWidgetsWTrendPeriod.sort(
    (a, b) => urlSortOrder[a.trendPeriod] - urlSortOrder[b.trendPeriod]
  );

  // Define trend period filters for different time ranges
  const trendPeriodFilters = {
    longTerm: [
      "trends-yearly",
      "trends-quarterly",
      "trends-monthly",
    ] as TrendPeriod[],
    mediumTerm: [
      "trends-quarterly",
      "trends-monthly",
      "trends-weekly",
    ] as TrendPeriod[],
    shortMediumTerm: ["trends-monthly", "trends-weekly"] as TrendPeriod[],
    shortTerm: ["trends-weekly", "trends-daily", "trends-hourly"] as TrendPeriod[],
    veryShortTerm: ["trends-daily", "trends-hourly"] as TrendPeriod[],
  };

  // Logic based on clear time period boundaries:
  // > 1 year (12 months) → longTerm
  // > 6 months and ≤ 1 year → mediumTerm
  // > 1 month and ≤ 6 months → shortMediumTerm
  // > 15 days and ≤ 1 month → shortTerm (Hourly stays available through a full month)
  // ≤ 15 days → veryShortTerm

  if (monthDiff > 12) {
    return childWidgetsWTrendPeriod.filter((d) =>
      trendPeriodFilters.longTerm.includes(d.trendPeriod)
    );
  } else if (monthDiff > 6 && monthDiff <= 12) {
    return childWidgetsWTrendPeriod.filter((d) =>
      trendPeriodFilters.mediumTerm.includes(d.trendPeriod)
    );
  } else if (monthDiff > 1 && monthDiff <= 6) {
    return childWidgetsWTrendPeriod.filter((d) =>
      trendPeriodFilters.shortMediumTerm.includes(d.trendPeriod)
    );
  } else if (monthDiff <= 1 && dayDiff > 15) {
    return childWidgetsWTrendPeriod.filter((d) =>
      trendPeriodFilters.shortTerm.includes(d.trendPeriod)
    );
  } else {
    return childWidgetsWTrendPeriod.filter((d) =>
      trendPeriodFilters.veryShortTerm.includes(d.trendPeriod)
    );
  }
};

export {
  sectionTabTitles,
  filterWidgetUrlsBySelectedTimePeriod,
  getSectionTabTitles,
  type TrendPeriod,
  type ChildWidget,
  type ChildWidgetWithTrendPeriod,
};
